import i18next from 'i18next';

export type ChannelKey = 'telus' | 'unmind';

export interface Channel {
  key: ChannelKey;
  brandName: string;
  productName: string;
  domain: string;
  supportUrl: string;
  privacyUrl: string;
  termsUrl: string;
  /**
   * Domain of the address the channel sends transactional email from
   * (`tbl_channel_partner.sender_address` in the Core API), used to filter
   * inbox search links. Matched by domain rather than full address so
   * back-office edits to the exact sender don't silently break those links.
   *
   * These are the PRODUCTION senders. The sender varies by environment as well
   * as by channel — UAT sends from `support@app-uat.unmindtech.com`, not
   * `support@unmind.com` — so a search filtered on this value returns nothing
   * on UAT and dev. That is a known and accepted gap (ENG-21800): production is
   * the only environment real users see, and it is verified working there.
   *
   * So: if you are testing an inbox shortcut on UAT and the search comes back
   * empty, this is why. It is not the original bug resurfacing.
   *
   * To close the gap, source the sender from the Core API `channel` GraphQL
   * query rather than here. It is unauthenticated, so it also works on
   * logged-out screens, and it returns the right sender per environment because
   * it reads the very row the email is sent from.
   *
   * Undefined when a channel's sender is unconfirmed: a wrong sender is worse
   * than none, since it turns a search into a guaranteed empty result set.
   * Callers must omit the filter rather than guess.
   */
  senderDomain?: string;
}

const channelConfigs: Record<
  ChannelKey,
  { domain: string; senderDomain?: string }
> = {
  unmind: { domain: 'unmind.com', senderDomain: 'unmind.com' },
  // senderDomain unconfirmed — the sender address is a DB value, and this
  // channel's is ambiguous between telus.com and telushealth.com from the repo
  // alone. Needs checking against the channel row before being filled in.
  telus: { domain: 'elevate.telushealth.com' },
};

export function getChannelKey(): ChannelKey {
  const envChannelName = process.env.REACT_APP_CHANNEL_NAME;

  if (!envChannelName) {
    throw new Error(
      'REACT_APP_CHANNEL_NAME is not set. Add it to your .env file (e.g. REACT_APP_CHANNEL_NAME=unmind).',
    );
  }

  return Object.keys(channelConfigs).some(k => k === envChannelName)
    ? (envChannelName as ChannelKey)
    : 'unmind';
}

/**
 * `skipInterpolation` is load-bearing. These values populate
 * `interpolation.defaultVariables` (see `i18n/config`), which i18next merges
 * into the data of every `t()` call — including this one. Interpolating here
 * would re-enter this lookup and recurse until the stack overflows.
 */
const readChannelValue = (
  field:
    | 'brandName'
    | 'productName'
    | 'links.support'
    | 'links.privacy'
    | 'links.terms',
): string =>
  i18next.t(`channels:${getChannelKey()}.${field}`, {
    skipInterpolation: true,
  });

export function getChannel(): Channel {
  const key = getChannelKey();

  return {
    key,
    domain: channelConfigs[key].domain,
    senderDomain: channelConfigs[key].senderDomain,
    brandName: readChannelValue('brandName'),
    productName: readChannelValue('productName'),
    supportUrl: readChannelValue('links.support'),
    privacyUrl: readChannelValue('links.privacy'),
    termsUrl: readChannelValue('links.terms'),
  };
}

/**
 * Merged into the data of every `t()` call, so any translation can reference
 * `{{brandName}}` / `{{productName}}` without its call site passing them. This
 * is what keeps the brand out of translated copy — see the `channels`
 * namespace for the values themselves.
 *
 * Getters rather than plain values, so the lookup stays lazy: it runs per call
 * and therefore tracks the active language.
 */
export const channelInterpolationDefaults = {
  get brandName(): string {
    return readChannelValue('brandName');
  },
  get productName(): string {
    return readChannelValue('productName');
  },
};

export function isTelusTheme(): boolean {
  return getChannel().key === 'telus';
}

export function hasMobileApp(): boolean {
  return getChannel().key === 'unmind';
}

// API, so can mock functions in tests
export const api = {
  getChannel,
};
